import { Injectable } from '@angular/core';
import { Subject } from 'rxjs';
import { LocalStorageService } from './localStorage.service';

@Injectable({
  providedIn: 'root'
})
export class NudgeService {
  private nudgeTimeouts: { [key: string]: any } = {};
  private nudgeVisibilitySubjects: { [key: string]: Subject<boolean> } = {};

  constructor(
    private _localStorage: LocalStorageService,
  ) {
    this.initializeNudgeStates();
  }

  /**
   * Initializes the state of nudges based on their stored dismiss times and durations in local storage.
   */
  private initializeNudgeStates(): void {
    const currentTime = new Date().getTime();
    for (const key in localStorage) {
      if (localStorage.hasOwnProperty(key) && key.startsWith('nudgeDismissTime_')) {
        const nudgeId = key.replace('nudgeDismissTime_', '');
        const dismissTime = parseInt(this._localStorage.get(key), 10);
        const elapsedTime = currentTime - dismissTime;
        const remainingTime = parseInt(this._localStorage.get(`nudgeDuration_${nudgeId}`), 10) - elapsedTime;

        if (!this.nudgeVisibilitySubjects[nudgeId]) {
          this.nudgeVisibilitySubjects[nudgeId] = new Subject<boolean>();
        }

        if (remainingTime > 0) {
          this.nudgeTimeouts[key] = setTimeout(() => {
            this.showNudge(nudgeId);
          }, remainingTime);
        } else {
          this._localStorage.remove(key);
          this._localStorage.remove(`nudgeDuration_${nudgeId}`);
          this.showNudge(nudgeId);
        }
      }
    }
  }

  /**
   * Shows a nudge by removing its dismiss time and duration from local storage and notifying subscribers.
   * @param {string} nudgeId - The identifier for the nudge.
   */
  showNudge(nudgeId: string): void {
    this._localStorage.remove(`nudgeDismissTime_${nudgeId}`);
    this._localStorage.remove(`nudgeDuration_${nudgeId}`);
    clearTimeout(this.nudgeTimeouts[`nudgeDismissTime_${nudgeId}`]);
    this.getNudgeVisibilitySubject(nudgeId).next(true);
  }

  /**
   * Dismisses a nudge for a specified duration by setting its dismiss time and duration in local storage and notifying subscribers.
   * @param {string} nudgeId - The identifier for the nudge.
   * @param {number} duration - The duration (in milliseconds) for which the nudge should be dismissed.
   */
  dismissNudge(nudgeId: string, duration: number): void {
    const dismissTime = new Date().getTime();
    this._localStorage.set(`nudgeDismissTime_${nudgeId}`, dismissTime.toString());
    this._localStorage.set(`nudgeDuration_${nudgeId}`, duration.toString());
    clearTimeout(this.nudgeTimeouts[`nudgeDismissTime_${nudgeId}`]);
    this.getNudgeVisibilitySubject(nudgeId).next(false);
    this.nudgeTimeouts[`nudgeDismissTime_${nudgeId}`] = setTimeout(() => {
      this.showNudge(nudgeId);
    }, duration); // duration in milliseconds
  }

  /**
   * Checks if a nudge is currently visible.
   * @param {string} nudgeId - The identifier for the nudge.
   * @returns {boolean} - True if the nudge is visible, false otherwise.
   */
  isNudgeVisible(nudgeId: string): boolean {
    return !this._localStorage.get(`nudgeDismissTime_${nudgeId}`);
  }

  /**
   * Gets the visibility subject for a specific nudge, creating it if it does not already exist.
   * @param {string} nudgeId - The identifier for the nudge.
   * @returns {Subject<boolean>} - The subject tracking the visibility state of the nudge.
   */
  getNudgeVisibilitySubject(nudgeId: string): Subject<boolean> {
    if (!this.nudgeVisibilitySubjects[nudgeId]) {
      this.nudgeVisibilitySubjects[nudgeId] = new Subject<boolean>();
    }
    return this.nudgeVisibilitySubjects[nudgeId];
  }

  /**
   * Clears all nudge-related data from local storage.
   */
  clearNudgeStorage(): void {
    for (const key in localStorage) {
      if (localStorage.hasOwnProperty(key) && (key.startsWith('nudgeDismissTime_') || key.startsWith('nudgeDuration_'))) {
        this._localStorage.remove(key);
      }
    }
  }
}
